import React, { useState, useRef } from 'react';

interface VoiceRecorderProps {
  onSave: (blob: Blob) => void;
  disabled?: boolean;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onSave, disabled = false }) => {
  const [recording, setRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunks.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        onSave(blob);
        setHasRecording(true);
        
        // Clean up the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const clearRecording = () => {
    setHasRecording(false);
    chunks.current = [];
  };

  return (
    <div style={{ 
      padding: '10px', 
      border: '1px solid #ddd', 
      borderRadius: '5px', 
      backgroundColor: '#f9f9f9' 
    }}>
      <div style={{ marginBottom: '10px' }}>
        <strong>🎤 Voice Note</strong>
        {hasRecording && (
          <span style={{ marginLeft: '10px', color: 'green', fontSize: '0.9em' }}>
            ✓ Recording saved
          </span>
        )}
      </div>
      
      {error && (
        <div style={{ 
          color: 'red', 
          fontSize: '0.9em', 
          marginBottom: '10px',
          padding: '5px',
          backgroundColor: '#ffe6e6',
          borderRadius: '3px'
        }}>
          {error}
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {recording ? (
          <button 
            onClick={stopRecording}
            style={{
              padding: '8px 15px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <span style={{ 
              width: '8px', 
              height: '8px', 
              backgroundColor: 'white', 
              borderRadius: '50%',
              animation: 'pulse 1s infinite'
            }}></span>
            Stop Recording
          </button>
        ) : (
          <button 
            onClick={startRecording}
            disabled={disabled}
            style={{
              padding: '8px 15px',
              backgroundColor: disabled ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
          >
            🎤 Record Voice Note
          </button>
        )}
        
        {hasRecording && (
          <button 
            onClick={clearRecording}
            style={{
              padding: '8px 15px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Recording
          </button>
        )}
      </div>
      
      {recording && (
        <div style={{ 
          marginTop: '10px', 
          fontSize: '0.9em', 
          color: '#666',
          fontStyle: 'italic'
        }}>
          🔴 Recording in progress... Click "Stop Recording" when finished.
        </div>
      )}
      
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};
